import React from 'react';
import classnames from 'classnames';
import { withTranslation, WithTranslation } from 'react-i18next';
import { PapiGallection } from '@wix/da-types-papi';
import { ALL_FOLDER_ID, SCRAPS_FOLDER_ID } from '@wix/da-url';
import { withMobileContext } from '@wix/da-shared-react/pkg/Context/MobileContext';
import Flex from '@wix/da-shared-react/pkg/Flex';
import SubfolderCard from '../../SubfolderCard';

import s from './FolderDescription.scss';

export interface Props {
  folder: PapiGallection;
  subFolders: PapiGallection[];
  className?: string;
  isMobile?: boolean;
}

export interface State {
  isExpanded: boolean;
}

const MAX_DESCRIPTION_LENGTH_FOR_MOBILE = 70;

// TODO: rename to FolderDescriptionCard
class FolderDescription extends React.Component<
  Props & WithTranslation,
  State
> {
  state = {
    isExpanded: false,
  };

  wrapRef: React.RefObject<HTMLDivElement> = React.createRef();

  render() {
    const {
      folder: { name },
      className,
    } = this.props;
    return (
      <div className={classnames(s['root'], className)}>
        <Flex direction="column">
          <h2 className={s['title']}>{name}</h2>
          {this.renderDescription()}
          {this.renderSubfolders()}
        </Flex>
      </div>
    );
  }

  renderDescription() {
    const description = this.getDescriptionText();
    if (!description) {
      return null;
    }

    const { isMobile, t } = this.props;
    const { isExpanded } = this.state;
    const isDescriptionCropped =
      this.isDescriptionCroppedOnMobile() ||
      this.isDescriptionCroppedOnDesktop();

    return (
      <div onClick={this.toggleExpand}>
        <div
          ref={this.wrapRef}
          className={classnames(
            s['description-wrap'],
            !isExpanded && !isMobile && s['collapsed']
          )}
        >
          <p className={s['description']}>
            {isDescriptionCropped && !isExpanded && isMobile
              ? description.slice(0, MAX_DESCRIPTION_LENGTH_FOR_MOBILE)
              : description}
            {isDescriptionCropped && !isExpanded && isMobile && <>... </>}
          </p>
          <div className={s['description-overlay']} />
        </div>
        {isDescriptionCropped && !isExpanded && (
          <a className={s['read-more']} href="" onClick={this.preventDefault}>
            {t!('pages.gallections.folderDescription.readMore')}
          </a>
        )}
      </div>
    );
  }

  isDescriptionCroppedOnMobile() {
    const { isMobile } = this.props;

    return (
      isMobile &&
      this.getDescriptionText().length > MAX_DESCRIPTION_LENGTH_FOR_MOBILE
    );
  }

  isDescriptionCroppedOnDesktop() {
    const { isMobile } = this.props;

    if (isMobile || !this.wrapRef.current) {
      return false;
    }

    const wrapHeight = this.wrapRef.current!.getBoundingClientRect().height;
    const descriptionHeight = this.wrapRef
      .current!.querySelector('p')!
      .getBoundingClientRect().height;
    return wrapHeight < descriptionHeight;
  }

  getDescriptionText = () => {
    const {
      folder: { description, folderId },
    } = this.props;

    if ([ALL_FOLDER_ID, SCRAPS_FOLDER_ID].includes(folderId)) {
      // pseudo-folders
      return '';
    }

    // Text without line breaks and html tags
    return (description || '').replace(/<\/?[^>]+>/g, '');
  };

  toggleExpand = () => this.setState({ isExpanded: !this.state.isExpanded });

  preventDefault = event => event.preventDefault();

  renderSubfolders = () => {
    const { t, subFolders } = this.props;
    if (!subFolders.length) {
      return null;
    }
    return (
      <div className={s['subfolders-section']}>
        <div className={s['subfolders-header']}>
          {t('pages.gallections.subfolders')}
        </div>
        <Flex wrap="wrap">
          {subFolders.map(subFolder => (
            <SubfolderCard
              key={subFolder.folderId}
              subfolder={subFolder}
              className={s['subfolder']}
            />
          ))}
        </Flex>
      </div>
    );
  };
}

export default withMobileContext(withTranslation()(FolderDescription));
